import { useRef, type ReactNode } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useAuth } from "../../lib/useAuth";
import {
  flattenHistory,
  joinedLabel,
  titlesLabel,
  useWatchHistory,
  useWatchStateSync,
  useWatchStats,
} from "../../lib/profile";
import { railEntries, useWatchlist } from "../../lib/watchlist";
import { useScrollFade } from "../../lib/useScrollFade";
import { discoverySource, toMediaItem } from "../../sources/discoverySource";
import { Rail, SEE_ALL_CLASS, SeeAllArrow } from "../../components/media";
import { MediaCard } from "../../components/library/media-card";
import { EmptyState } from "../../components/page-state";
import { AvatarTile } from "../../components/profile/avatar-tile";
import {
  HistoryCard,
  HistoryEmptyState,
  RailSkeleton,
} from "../../components/profile/history-cards";
import { WatchStatsStrip } from "../../components/profile/stats-strip";
import { Chip, ErrorNote } from "../../components/ui";
import { BookmarkIcon } from "../../components/icons";

export const Route = createFileRoute("/_authed/profile")({
  component: Profile,
});

/** How many cards a rail shows before "See all". */
const RAIL_CARDS = 12;

/**
 * The account's profile (the avatar menu's Profile), over the settings'
 * blurred still: one frosted panel — the initial tile, display name and
 * @username, Joined + sync chips, Edit profile / Settings — with the
 * "watched since joining" strip beside it (movies, shows, episodes, hours),
 * then a rail of the latest watched titles and one of the account's
 * watchlist straight on the backdrop (Plex's My Watch History / My
 * Watchlist stack). Everything here is account-level (the cloud's watch
 * history the servers mirror into, the TMDB-keyed watchlist), not the
 * active server's. The panel and rails dissolve under the bar as the page
 * scrolls (.rago-scroll-fade), which is why the backdrop is a sibling.
 */
function Profile() {
  const state = useAuth();
  const user = state.status === "signedIn" ? state.user : null;
  const sync = useWatchStateSync();
  const history = useWatchHistory();
  const stats = useWatchStats();
  const wl = useWatchlist();
  const shellRef = useRef<HTMLDivElement>(null);
  useScrollFade(shellRef);
  if (!user) return null;

  const syncKnown = sync.data !== undefined;
  const enabled = sync.data?.enabled ?? false;
  const entries = flattenHistory(history.data).slice(0, RAIL_CARDS);
  const joined = joinedLabel(user.createdAt);
  // The rail's count is the account's distinct titles (the stats), not the
  // dozen cards it shows.
  const historyTitles = stats.data ? titlesLabel(stats.data.movies + stats.data.episodes) : undefined;

  let rail: ReactNode;
  if (history.isLoading || sync.isLoading) {
    rail = <RailSkeleton />;
  } else if (history.isError) {
    rail = (
      <div className="px-6 sm:px-10">
        <ErrorNote error={history.error} />
      </div>
    );
  } else if (entries.length === 0) {
    // The consent unknown (an old broker) reads as on: the off copy would
    // send people to a switch that may not exist.
    rail = <HistoryEmptyState syncOn={enabled || !syncKnown} compact />;
  } else {
    rail = (
      <Rail
        title="Watch history"
        meta={historyTitles}
        caption={
          syncKnown && !enabled ? "Sync is off — new plays aren't being recorded." : undefined
        }
        count={entries.length}
        action={
          <Link to="/profile/history" className={SEE_ALL_CLASS}>
            See all
            <SeeAllArrow />
          </Link>
        }
      >
        {entries.map((e) => (
          <HistoryCard key={e.id} entry={e} />
        ))}
      </Rail>
    );
  }

  // The watchlist rail: the same cards the /watchlist page draws (poster,
  // "N seasons" or the year, Discovery deep link), newest save first.
  const { entries: saved, unavailable, total } = railEntries(wl.data, RAIL_CARDS);
  let watchlistRail: ReactNode;
  if (wl.isLoading) {
    watchlistRail = <RailSkeleton portrait />;
  } else if (wl.isError) {
    watchlistRail = (
      <div className="px-6 sm:px-10">
        <ErrorNote error={wl.error} />
      </div>
    );
  } else if (saved.length === 0) {
    watchlistRail = (
      <EmptyState
        icon={<BookmarkIcon size={22} />}
        title="Your watchlist is empty"
        compact
        action={
          <Link to="/discover" className="rago-btn rago-btn-fit">
            Browse Discovery
          </Link>
        }
      >
        Save films and shows from Discovery and they&apos;ll gather here.
      </EmptyState>
    );
  } else {
    watchlistRail = (
      <Rail
        title="Watchlist"
        meta={titlesLabel(total)}
        caption={unavailable > 0 ? "Some saved titles couldn't be loaded right now." : undefined}
        count={saved.length}
        action={
          <Link to="/watchlist" className={SEE_ALL_CLASS}>
            See all
            <SeeAllArrow />
          </Link>
        }
      >
        {saved.map((e) => (
          <MediaCard
            key={`${e.mediaType}:${e.tmdbId}`}
            item={toMediaItem(e.item)}
            source={discoverySource}
            className="w-[152px] shrink-0 snap-start sm:w-[176px]"
          />
        ))}
      </Rail>
    );
  }

  return (
    <div ref={shellRef} className="rago-profile-shell rago-scroll-fade">
      <div className="px-6 sm:px-10">
        <section aria-label="Profile" className="rago-panel rago-rise">
          <div className="flex flex-col gap-6 p-6 sm:p-8 lg:flex-row lg:items-center lg:gap-10">
            <div className="flex min-w-0 flex-1 items-start gap-5 sm:gap-7">
              <AvatarTile name={user.displayName || user.username} />
              <div className="min-w-0 pt-1">
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h1 className="rago-display max-w-full truncate text-[2rem] leading-none sm:text-[2.75rem]">
                    {user.displayName}
                  </h1>
                  <p className="max-w-full truncate text-base text-[var(--rago-text-3)]">
                    @{user.username}
                  </p>
                </div>
                <div className="mt-3.5 flex flex-wrap items-center gap-2">
                  {joined ? <Chip size="md">{joined}</Chip> : null}
                  {syncKnown ? (
                    <Chip size="md" tone={enabled ? "ok" : "muted"}>
                      {enabled ? "Sync on" : "Sync off"}
                    </Chip>
                  ) : null}
                </div>
                <div className="mt-5 flex flex-wrap gap-2.5">
                  <Link
                    to="/settings/account"
                    hash="displayName"
                    className="rago-btn rago-btn-quiet select-none"
                  >
                    Edit profile
                  </Link>
                  <Link to="/settings/account" className="rago-btn rago-btn-quiet select-none">
                    Settings
                  </Link>
                </div>
              </div>
            </div>
            <WatchStatsStrip
              stats={stats.data}
              loading={stats.isLoading}
              className="border-t border-[var(--rago-line-1)] pt-6 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-10"
            />
          </div>
        </section>
      </div>

      <div className="rago-rise rago-rise-2 mt-10">{rail}</div>
      <div className="rago-rise rago-rise-3 mt-2">{watchlistRail}</div>
    </div>
  );
}
